import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, ChevronDown, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { LanguageSelector } from "@/components/ui/LanguageSelector";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLeadSubmission } from "@/hooks/useLeadSubmission";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "" });
  const { t } = useLanguage();
  const { submitLead, isSubmitting } = useLeadSubmission({ formType: "callback-modal" });

  const marketingItems = [
    { name: t("nav.smm"), href: "/marketing/smm", description: t("nav.smm.desc") },
    { name: t("nav.research"), href: "/marketing/research", description: t("nav.research.desc") },
  ];

  const solutionsItems = [
    { name: t("nav.crm"), href: "/solutions/crm", description: t("nav.crm.desc") },
    { name: t("nav.automation"), href: "/solutions/automation", description: t("nav.automation.desc") },
    { name: t("nav.bi"), href: "/solutions/bi-dashboards", description: t("nav.bi.desc") },
    { name: t("nav.custom"), href: "/solutions/custom", description: t("nav.custom.desc") },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || formData.phone.length < 17) {
      return;
    }

    const success = await submitLead(formData);
    
    if (success) {
      setFormData({ name: "", phone: "" });
      setCallbackOpen(false);
    }
  };

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <nav className="container flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <span className="text-2xl font-bold text-primary">Binar+</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:gap-6">
            {/* Marketing Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-1">
                  {t("nav.marketing")}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 bg-background z-50">
                {marketingItems.map((item) => (
                  <DropdownMenuItem key={item.href} asChild>
                    <Link to={item.href} className="flex flex-col items-start py-2">
                      <span className="font-medium">{item.name}</span>
                      <span className="text-sm text-muted-foreground">{item.description}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Solutions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-1">
                  {t("nav.solutions")}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 bg-background z-50">
                {solutionsItems.map((item) => (
                  <DropdownMenuItem key={item.href} asChild>
                    <Link to={item.href} className="flex flex-col items-start py-2">
                      <span className="font-medium">{item.name}</span>
                      <span className="text-sm text-muted-foreground">{item.description}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Phone Number */}
            <a 
              href="tel:+998901178111" 
              className="flex items-center gap-2 text-foreground hover:text-primary transition-colors font-medium"
            >
              <Phone className="h-4 w-4" />
              +998 90 117 81 11
            </a>

            {/* Callback Button */}
            <Button onClick={() => setCallbackOpen(true)}>
              {t("nav.callback")}
            </Button>

            {/* Language Selector */}
            <LanguageSelector />
          </div>

          {/* Mobile Menu */}
          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSelector />
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <div className="flex flex-col space-y-6 mt-8">
                  {/* Marketing Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-primary mb-3">{t("nav.marketing")}</h3>
                    <div className="space-y-2 pl-4">
                      {marketingItems.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block py-2 text-foreground hover:text-primary transition-colors"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Solutions Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-primary mb-3">{t("nav.solutions")}</h3>
                    <div className="space-y-2 pl-4">
                      {solutionsItems.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block py-2 text-foreground hover:text-primary transition-colors"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Phone Number */}
                  <a 
                    href="tel:+998901178111" 
                    className="flex items-center gap-2 text-foreground hover:text-primary transition-colors font-medium pl-4"
                  >
                    <Phone className="h-4 w-4" />
                    +998 90 117 81 11
                  </a>

                  {/* Callback */}
                  <Button 
                    className="mt-4"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setCallbackOpen(true);
                    }}
                  >
                    {t("nav.callback")}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </header>

      {/* Callback Modal */}
      <Dialog open={callbackOpen} onOpenChange={setCallbackOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("callback.title")}</DialogTitle>
            <DialogDescription>{t("callback.description")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="callback-name">{t("callback.name")} *</Label>
              <Input
                id="callback-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t("callback.namePlaceholder")}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="callback-phone">{t("callback.phone")} *</Label>
              <PhoneInput
                id="callback-phone"
                value={formData.phone}
                onChange={(value) => setFormData({ ...formData, phone: value })}
              />
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t("callback.sending") : t("nav.callback")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
